import { useI18n } from '@change-corgi/core/react/i18n';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import type { PaymentMethod } from 'src/app/pages/promote/shared/types';
import { useCountryCode } from 'src/app/shared/hooks/l10n';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useSession } from 'src/app/shared/hooks/session';
import type { PaymentType as TrackingPaymentMethod } from 'src/app/shared/utils/payments';
import { calculateImpressions } from 'src/app/shared/utils/promote';

import type { StripeGatewayData } from './api';
import { createPromotionCharge } from './api';
import { useLineItemDescription } from './useLineItemDescription';

export function useCreatePromotion(): (
	paymentMethod: PaymentMethod,
	trackingPaymentMethod: TrackingPaymentMethod,
) => Promise<StripeGatewayData | undefined> {
	const utilityContext = useUtilityContext();
	const { amountToBaseUnits } = useI18n();
	const [{ amount, email, isPublic }] = usePromotePageState();
	const {
		data: { currency, petition, promotionSettings },
	} = usePromotePageData();
	const countryCode = useCountryCode();
	const session = useSession();
	const sourceLocation = useQueryParamValue('source_location');
	const currentSource = useQueryParamValue('utm_source') || session.trackingData.current_source;
	const lineItemDescription = useLineItemDescription();

	return async function create(paymentMethod, trackingPaymentMethod) {
		const result = await createPromotionCharge(
			{
				amount: amountToBaseUnits(amount, currency.code),
				currencyCode: currency.code,
				customer: {
					countryCode,
					email,
				},
				paymentMethod,
				productType: 'promotion',
				lineItemDescription: lineItemDescription(trackingPaymentMethod),
			},
			{
				impressionsRequested: calculateImpressions(
					amountToBaseUnits(amount, currency.code),
					promotionSettings.cpm,
					promotionSettings.bonusCpm,
					promotionSettings.bonusThreshold,
				),
				petitionId: petition.id,
				validationToken: promotionSettings.promotionToken,
				visibility: isPublic ? 'PUBLIC' : 'PRIVATE',
				currentSource,
				sourceLocation,
			},
			utilityContext,
		);
		return result;
	};
}
